import React, { useState, ChangeEvent } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import { simpleGetter } from "../../../actions/common";
import { universalApi } from "../../../api/api";
import ArrowLeftWithBody from "../../../assets/icons/ArrowLeftWithBody";
import ArrowRight from "../../../assets/icons/ArrowRight";
import CheckMark from "../../../assets/icons/CheckMark";
import Delete from "../../../assets/icons/Delete";
import Dot from "../../../assets/icons/Dot";
import QR from "../../../assets/icons/QR";
import Calendar from "../../../assets/icons/Calendar";
import Users from "../../../assets/icons/Users";
import CabsMenu from "../../../assets/icons/CabsMenu";
import ListBullet from "../../../assets/icons/ListBullet";
import activityHours from "../../../assets/png/activity-hours.png";
import maleStudentProfile from "../../../assets/png/male-student-profile.png";
import roomStars from "../../../assets/svg/room-stars.svg";
import Modal from "../../../components/Modal/Modal";
import DefaultButton from "../../../components/default-button/DefaultButton";
import TabWrapper from "../../../components/Schedule/TabWrapper";
import { useRooms } from "../../../hooks/cabinet";
import { showSnackbar } from "../../../misc/utils";
import SchedulePage from "../Schedule";
import SubjectsPage from "../../../components/Subjects/SubjectsPage";
import AddSubjectLessonModal from "../../../components/Subjects/AddSubjectLessonModal";

interface TeacherData {
  firstname: string;
  lastname: string;
}

interface Lesson {
  teacher_data?: TeacherData;
}

interface Room {
  id: number;
  name: string;
  lessons?: Lesson[];
}

interface TabItem {
  key: string;
  label: string;
  icon: React.ComponentType<any>; // Change this line
}

type TabKey = "schedule" | "subjects" | "schools" | "cabinets";

interface ApiResponse {
  item?: any;
  success?: boolean;
}

const IconWrapper: React.FC<{ Icon: React.ComponentType<any>; additionalStyle?: string }> = ({ 
  Icon, 
  additionalStyle 
}) => <Icon additionalStyle={additionalStyle} />;

export const CabinetsNew: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>("");
  const [currentRoom, setCurrentRoom] = useState<Room>({} as Room);
  const [qrModalOpen, setQrModalOpen] = useState<boolean>(false);
  const [createCabinetModaOpen, setCreateCabinetModaOpen] = useState<boolean>(false);
  const [newCabinetName, setNewCabinetName] = useState<string>("");
  const [triggerRoomsFetch, setTriggerRoomsFetch] = useState<number>(0);
  const [currentTab, setCurrentTab] = useState<TabKey>("cabinets");
  const [addSubjectLessonModalOpen, setAddSubjectLessonModalOpen] = useState<boolean>(false);

  // Transition classes for smooth animations
  const transitionClasses = {
    tabSwitch: "transition-all duration-300 ease-in-out",
    cardHover: "transition-all duration-200 ease-in-out",
    modalTransition: "transition-all duration-300 ease-in-out",
    buttonHover: "transition-all duration-200 ease-in-out"
  };
  
  const rooms: Room[] = useRooms(search, triggerRoomsFetch, false);

  const tabs: TabItem[] = [
    { key: "schedule", label: "Расписание", icon: (props) => <IconWrapper Icon={ListBullet} {...props} /> },
    { key: "subjects", label: "Предметы", icon: (props) => <IconWrapper Icon={Calendar} {...props} /> },
    { key: "schools", label: "Школы", icon: (props) => <IconWrapper Icon={Users} {...props} /> },
    { key: "cabinets", label: "Кабинеты", icon: (props) => <IconWrapper Icon={CabsMenu} {...props} /> },
  ];

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const goToRoom = (roomId: number) => (): void => {
    navigate(`/admin/cabinets/${roomId}`);
  };

  const openQrModal = (room: Room) => (): void => {
    setCurrentRoom(room);
    setQrModalOpen(true);
  };

  const onNewCabinetNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setNewCabinetName(e.target.value);
  };

  const toggelCreateCabinetModal = (): void => {
    setCreateCabinetModaOpen(!createCabinetModaOpen);
  };

  const onAdd = async (): Promise<void> => {
    const response: ApiResponse = await simpleGetter(universalApi.create, {
      model: "Room",
      body: {
        create: {
          name: newCabinetName,
          active: 1,
        },
      },
    });
    showSnackbar(
      response.item ? "Успешно" : "Произошла ошибка",
      response.item ? "success" : "error"
    );
    if (response.item) {
      setTriggerRoomsFetch((t: number) => t + 1);
      toggelCreateCabinetModal();
      setNewCabinetName("");
    }
  };

  const onDelete = (room: Room) => async (): Promise<void> => {
    if (!window.confirm(`Вы уверены, что хотите удалить кабинет "${room.name}"?`)) {
      return;
    }
    
    const response: ApiResponse = await simpleGetter(universalApi.delete, {
      model: "Room",
      body: {
        filters: {
          id: room.id,
        },
        update: {
          active: 0,
        },
      },
    });
    showSnackbar(
      response.success ? "Кабинет успешно удален" : "Произошла ошибка",
      response.success ? "success" : "error"
    );
    if (response.success) {
      setTriggerRoomsFetch((t: number) => t + 1);
    }
  };

  const isAddDisabled: boolean = !newCabinetName;

  return (
    <div className="p-4 bg-[#f8f9fd] h-full flex flex-col">
      <div className="mb-4 bg-white py-2 px-4 rounded-lg w-full flex-shrink-0">
        <div className="overflow-x-auto">
          <TabWrapper
            tabs={tabs}
            currentTab={currentTab}
            onChange={(k: string) => setCurrentTab(k as TabKey)}
            className={transitionClasses.tabSwitch}
          />
        </div>
        {currentTab === "subjects" && (
          <div className="mt-3 flex justify-end">
            <DefaultButton
              onClick={() => setAddSubjectLessonModalOpen(true)}
              additionalStyle="bg-valley-dark-green text-white px-4 py-2 w-full sm:w-auto"
              buttonText="Добавить предмет и урок преподавателю"
            />
          </div>
        )}
      </div>

      <div className="w-full bg-white flex-1 min-h-0 py-2 px-4 rounded-lg flex flex-col">
        {currentTab === "cabinets" && (
          <>
            <div className="flex flex-col sm:flex-row sm:justify-between mb-4 gap-3 sm:gap-0">
              <input
                className="bg-[#F0F3FA] text-[#1A1A1A] w-full sm:w-[200px] h-10 rounded-lg px-4 py-[10.5px]"
                placeholder="Поиск по кабинетам"
                value={search}
                onChange={onChangeInput}
              />
              <DefaultButton
                onClick={toggelCreateCabinetModal}
                additionalStyle='bg-valley-dark-green text-white px-3 w-full sm:w-auto'
                buttonText='Создать кабинет'
              />
            </div>
            <div className="hidden lg:flex flex-row border-b border-b-[$F0F3FA] pt-2 pb-4 flex-shrink-0">
              <div className="w-[17%] pl-2 flex flex-row justify-center">
                <span className="text-[#5F6B88] font-medium text-sm">Кабинет</span>
              </div>
              <div className="w-[15%] pl-2 flex flex-row">
                <span className="text-[#5F6B88] font-medium text-sm">
                  Статус кабинета
                </span>
              </div>
              <div className="w-[6%] pl-2 flex flex-row justify-center ">
                <span className="text-[#5F6B88] font-medium text-sm">QR</span>
              </div>
              <div className="w-[19%] pl-2 flex flex-row">
                <span className="text-[#5F6B88] font-medium text-sm">
                  Последний преподаватель
                </span>
              </div>
              <div className="w-[25%] pl-2 flex flex-row justify-center">
                <span className="text-[#5F6B88] font-medium text-sm">
                  Часы активности
                </span>
              </div>
              <div className="w-[17%] pl-2 flex flex-row">
                <span className="text-[#5F6B88] font-medium text-sm">Рейтинг</span>
              </div>
            </div>
            {/* Десктопная версия списка */}
            <div className="hidden lg:block overflow-y-auto flex-1 min-h-0">
              {rooms.map((r: Room, i: number) => (
                <div key={i} className={`flex flex-row border-b border-b-[$F0F3FA] py-3 ${transitionClasses.cardHover} hover:bg-gray-50`}>
                  <div
                    className="cursor-pointer w-[17%] pl-2 flex flex-row items-center"
                    onClick={() => navigate(`/admin/cabinets/${r.id}`)}
                  >
                    <ArrowRight additionalStyle="text-blue-500" />
                    <span className="ml-4 font-medium text-[15px] text-[#1A1A1A]">
                      {r.name}
                    </span>
                  </div>
                  <div className="w-[15%] pl-2 flex flex-row items-center">
                    <span className="text-[#5F6B88] font-medium text-sm">
                      {Boolean(r.lessons?.length) ? (
                        <div className="flex flex-row gap-1">
                          <Dot additionalStyle="text-green-500" />
                          <span className="ml-1 text-[15px] font-medium text-[#3BBC30]">
                            Активный
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-row gap-1">
                          <Dot additionalStyle="text-gray-300" />
                          <span className="ml-1 text-[15px] font-medium text-[#AFB7CA]">
                            Неактивный
                          </span>
                        </div>
                      )}
                    </span>
                  </div>
                  <div className="w-[7%] pl-2 flex flex-row justify-center items-center">
                    <DefaultButton
                      Icon={QR}
                      onClick={openQrModal(r)}
                      additionalStyle="bg-gray-100 text-gray-500 px-2"
                    />
                  </div>
                  <div className="w-[19%] pl-2 flex flex-row items-center">
                    {Boolean(r.lessons?.length) &&
                      Boolean(r.lessons?.find((l: Lesson) => Boolean(l.teacher_data))) && (
                        <div className="flex flex-row items-center">
                          <img src={maleStudentProfile} className="w-10 h-10" alt="Teacher" />
                          <span className="ml-3 text-[15px] font-medium text-[#1A1A1A]">
                            {
                              r.lessons?.find((l: Lesson) => Boolean(l.teacher_data))
                                ?.teacher_data?.firstname
                            }{" "}
                            {
                              r.lessons?.find((l: Lesson) => Boolean(l.teacher_data))
                                ?.teacher_data?.lastname
                            }
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="w-[25%] pl-2 flex flex-row items-center justify-center">
                    <img className="h-[58px]" src={activityHours} alt="Activity hours" />
                  </div>
                  <div className="w-[17%] pl-2 flex flex-row">
                    <img src={roomStars} alt="Room rating" />
                    <div className="flex items-center">
                      <DefaultButton
                        Icon={Delete}
                        onClick={onDelete(r)}
                        additionalStyle="ml-8 text-red-500 px-2 bg-red-50"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Мобильная версия (карточки) */}
            <div className="lg:hidden space-y-3 overflow-y-auto flex-1 min-h-0">
              {rooms.map((r: Room, i: number) => (
                <div key={i} className={`border border-[#F0F3FA] bg-white rounded-lg p-4 ${transitionClasses.cardHover} hover:shadow-md`}>
                  <div className="flex items-center justify-between">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() => navigate(`/admin/cabinets/${r.id}`)}
                    >
                      <ArrowRight additionalStyle="text-blue-500" />
                      <span className="ml-3 font-semibold text-[15px] text-[#1A1A1A]">{r.name}</span>
                    </div>
                    <DefaultButton
                      Icon={QR}
                      onClick={openQrModal(r)}
                      additionalStyle="bg-gray-100 text-gray-500 px-2"
                    />
                  </div>

                  <div className="mt-3 grid grid-cols-1 xs:grid-cols-2 gap-3">
                    <div className="flex items-center">
                      {Boolean(r.lessons?.length) ? (
                        <div className="flex flex-row items-center gap-1">
                          <Dot additionalStyle="text-green-500" />
                          <span className="text-[14px] font-medium text-[#3BBC30]">Активный</span>
                        </div>
                      ) : (
                        <div className="flex flex-row items-center gap-1">
                          <Dot additionalStyle="text-gray-300" />
                          <span className="text-[14px] font-medium text-[#AFB7CA]">Неактивный</span>
                        </div>
                      )}
                    </div>

                    {Boolean(r.lessons?.length) &&
                      Boolean(r.lessons?.find((l: Lesson) => Boolean(l.teacher_data))) && (
                        <div className="flex items-center">
                          <img src={maleStudentProfile} className="w-8 h-8" alt="Teacher" />
                          <span className="ml-2 text-[14px] font-medium text-[#1A1A1A]">
                            {
                              r.lessons?.find((l: Lesson) => Boolean(l.teacher_data))
                                ?.teacher_data?.firstname
                            }{" "}
                            {
                              r.lessons?.find((l: Lesson) => Boolean(l.teacher_data))
                                ?.teacher_data?.lastname
                            }
                          </span>
                        </div>
                      )}
                  </div>

                  <div className="mt-3 flex items-center justify-center">
                    <img className="h-[48px]" src={activityHours} alt="Activity hours" />
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <img className="h-[18px]" src={roomStars} alt="Room rating" />
                    <DefaultButton
                      Icon={Delete}
                      onClick={onDelete(r)}
                      additionalStyle="text-red-500 px-2 bg-red-50"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {currentTab === "schedule" && (
          <SchedulePage />
        )}

        {currentTab === "subjects" && (
          <div className="p-4 flex flex-col">
            <SubjectsPage/>
          </div>
        )}

        {currentTab === "schools" && (
          <div className="p-4 text-sm text-valley-gray">
            Содержимое вкладки «Школы» 
          </div>
        )}
      </div>

      <Modal
        visible={qrModalOpen}
        setVisible={setQrModalOpen}
        additionalClassName={`w-2/5 rounded-[10px] p-7 ${transitionClasses.modalTransition}`}
      >
        <div className="flex flex-row justify-center">
          <QRCode
            value={JSON.stringify({
              type: "CreateLesson",
              roomName: currentRoom.name,
              roomId: currentRoom.id,
            })}
          />
        </div>
      </Modal>
      <AddSubjectLessonModal
        visible={addSubjectLessonModalOpen}
        setVisible={setAddSubjectLessonModalOpen}
        onSuccess={() => {
          setAddSubjectLessonModalOpen(false);
          setTriggerRoomsFetch((t) => t + 1);
        }}
      />
      <Modal
        visible={createCabinetModaOpen}
        setVisible={setCreateCabinetModaOpen}
        additionalClassName={`w-2/5 h-1/3 rounded-xl ${transitionClasses.modalTransition}`}
      >
        <div className="flex flex-col p-2">
          <div className="flex flex-col px-6">
            <span className="text-2xl text-[#1A1A1A] mb-8">
              Название кабинета
            </span>
            <span className="font-medium text-sm text-[#5F6B88] mb-2">
              Кабинет
            </span>
            <input
              className="admin-input mb-8"
              value={newCabinetName}
              onChange={onNewCabinetNameChange}
            />
            <div className="flex flex-row">
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#5F6B88] border py-3 mr-3 ${transitionClasses.buttonHover} hover:bg-[#4a5568]`}
                onClick={toggelCreateCabinetModal}
              >
                <ArrowLeftWithBody additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Вернуться
                </span>
              </button>
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#3BBC30] border py-3 ${transitionClasses.buttonHover} ${isAddDisabled ? "opacity-30" : ""
                  }`}
                onClick={onAdd}
                disabled={isAddDisabled}
              >
                <CheckMark additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Создать кабинет
                </span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
